import type { FileData } from "../types";
import { isPlaceholderProjectThumbnail } from "./filePathUtils";
import { sanitizeThumbnailTraceUrl } from "./thumbnailTrace";

export const INITIAL_RECENT_THUMBNAIL_PRELOAD_COUNT = 8;

const MAX_REMEMBERED_THUMBNAILS = 200;
const MAX_PRELOADED_URLS = 200;

const lastValidThumbnailBySource = new Map<string, string>();
const inFlightThumbnailRequests = new Map<string, Promise<string>>();
const preloadedThumbnailUrls = new Set<string>();

const trimOldestEntry = <T>(collection: Map<string, T> | Set<string>): void => {
  const oldestKey = collection.keys().next().value as string | undefined;
  if (oldestKey !== undefined) {
    collection.delete(oldestKey);
  }
};

export const getFinalFileThumbnailUrl = (
  file: FileData | null | undefined,
): string | null =>
  file?.thumbnailUrl || file?.thumbnail_url || file?.thumbnail || null;

export const normalizeFinalFile = (file: FileData): FileData => ({
  ...file,
  thumbnailUrl: getFinalFileThumbnailUrl(file) || null,
});

export const normalizeFinalFiles = (files?: FileData[]): FileData[] =>
  (files || []).map(normalizeFinalFile);

/**
 * Presigned query strings and list refetches are request identity, not image identity.
 * Prefer the durable project id so a move between Recent and Trash keeps one display
 * source while the storage path and signature are being refreshed.
 */
export const getFinalFileThumbnailSourceKey = (
  file: FileData | null | undefined,
  explicitThumbnailUrl?: string | null,
  ownerKey?: string | null,
): string => {
  const ownerPrefix = ownerKey ? `owner:${ownerKey}:` : "";
  if (file?.id != null && String(file.id).trim()) {
    return `${ownerPrefix}file:${String(file.id).trim()}`;
  }

  const path = file?.gcs_path || file?.full_path || file?.path || "";
  if (path) {
    const stablePath = (sanitizeThumbnailTraceUrl(path) || path).replace(
      /\/trash\//i,
      "/",
    );
    return `${ownerPrefix}path:${stablePath}`;
  }

  const thumbnailUrl = explicitThumbnailUrl || getFinalFileThumbnailUrl(file);
  if (thumbnailUrl) {
    return `${ownerPrefix}thumbnail:${
      sanitizeThumbnailTraceUrl(thumbnailUrl) || thumbnailUrl
    }`;
  }

  return `${ownerPrefix}thumbnail:missing`;
};

export const getRememberedFinalFileThumbnail = (
  sourceKey: string,
): string | null => lastValidThumbnailBySource.get(sourceKey) || null;

export const rememberValidFinalFileThumbnail = (
  sourceKey: string,
  url: string,
): void => {
  if (
    !sourceKey ||
    !url ||
    url.startsWith("data:") ||
    url.startsWith("/Images/") ||
    isPlaceholderProjectThumbnail(url, null)
  ) {
    return;
  }

  // Reinsertion keeps the bounded map ordered by most recently loaded source.
  lastValidThumbnailBySource.delete(sourceKey);
  lastValidThumbnailBySource.set(sourceKey, url);
  while (lastValidThumbnailBySource.size > MAX_REMEMBERED_THUMBNAILS) {
    trimOldestEntry(lastValidThumbnailBySource);
  }
};

/** Share concurrent signer work across StrictMode effects and tab remounts. */
export const resolveFinalFileThumbnailOnce = (
  requestKey: string,
  resolve: () => Promise<string>,
): Promise<string> => {
  const active = inFlightThumbnailRequests.get(requestKey);
  if (active) {
    return active;
  }

  const request = Promise.resolve()
    .then(resolve)
    .finally(() => {
      if (inFlightThumbnailRequests.get(requestKey) === request) {
        inFlightThumbnailRequests.delete(requestKey);
      }
    });
  inFlightThumbnailRequests.set(requestKey, request);
  return request;
};

/**
 * Start browser fetch/decode for the first two desktop rows before cards settle.
 * This is deliberately fire-and-forget: list rendering and sibling images never wait
 * for one thumbnail to load or fail, while the browser still deduplicates the card GET.
 */
export const preloadInitialFinalFileThumbnails = (
  files: FileData[],
  limit = INITIAL_RECENT_THUMBNAIL_PRELOAD_COUNT,
  ownerKey?: string | null,
): void => {
  if (typeof Image === "undefined" || limit <= 0) {
    return;
  }

  files.slice(0, limit).forEach((file) => {
    const url = getFinalFileThumbnailUrl(file);
    if (!url || preloadedThumbnailUrls.has(url)) {
      return;
    }

    preloadedThumbnailUrls.add(url);
    while (preloadedThumbnailUrls.size > MAX_PRELOADED_URLS) {
      trimOldestEntry(preloadedThumbnailUrls);
    }

    const sourceKey = getFinalFileThumbnailSourceKey(file, url, ownerKey);
    const image = new Image();
    image.onload = () => {
      rememberValidFinalFileThumbnail(sourceKey, url);
    };
    image.onerror = () => {
      // A later refreshed signature may be retried; this failure does not gate siblings.
      preloadedThumbnailUrls.delete(url);
    };
    image.src = url;
  });
};

/** Test isolation for module-level display caches. */
export const resetFinalFileThumbnailCachesForTests = (): void => {
  lastValidThumbnailBySource.clear();
  inFlightThumbnailRequests.clear();
  preloadedThumbnailUrls.clear();
};
